// /auth/cli — handles the `cairn login` device-flow handoff.
//
// 1. Reads ?port + ?state from the URL.
// 2. If user is not signed in, routes them through /auth/login with
//    ?returnTo=/auth/cli?... so they return here after signing in.
// 3. Once signed in, grabs the Firebase ID token + refresh token and
//    POSTs them to http://127.0.0.1:<port>/callback along with the
//    state (CSRF protection).
// 4. Shows status: signing in, posting to CLI, success, error.

import { useEffect, useState } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { auth } from '../../lib/firebase.js'

const STATUS = {
  IDLE: 'idle',
  POSTING: 'posting',
  SUCCESS: 'success',
  ERROR: 'error',
  MISSING_PARAMS: 'missing-params',
  NOT_SIGNED_IN: 'not-signed-in',
}

export function CliCallback() {
  const [params] = useSearchParams()
  const port = params.get('port')
  const state = params.get('state')
  const [user, authLoading] = useAuthState(auth)
  const [status, setStatus] = useState(STATUS.IDLE)
  const [errorMsg, setErrorMsg] = useState(null)

  // Validate URL params first
  useEffect(() => {
    if (!port || !state) {
      setStatus(STATUS.MISSING_PARAMS)
    }
  }, [port, state])

  // After auth resolves, drive the callback flow
  useEffect(() => {
    if (authLoading) return
    if (!port || !state) return
    if (!user) {
      setStatus(STATUS.NOT_SIGNED_IN)
      return
    }
    let cancelled = false
    ;(async () => {
      setStatus(STATUS.POSTING)
      try {
        const idToken = await user.getIdToken(true)
        const refreshToken = user.refreshToken
        const resp = await fetch(`http://127.0.0.1:${port}/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            state,
            idToken,
            refreshToken,
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            expiresInSec: 3600,
          }),
        })
        if (cancelled) return
        if (!resp.ok) {
          const body = await resp.text().catch(() => '<no body>')
          setErrorMsg(`CLI server returned ${resp.status}: ${body}`)
          setStatus(STATUS.ERROR)
          return
        }
        setStatus(STATUS.SUCCESS)
      } catch (err) {
        if (cancelled) return
        setErrorMsg(
          `Could not reach the CLI's local server at 127.0.0.1:${port}. ` +
            `Make sure \`cairn login\` is still running. (${err.message})`,
        )
        setStatus(STATUS.ERROR)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [authLoading, user, port, state])

  if (status === STATUS.MISSING_PARAMS) {
    return (
      <Body
        title="Invalid CLI sign-in URL"
        body="This URL is missing the required parameters. Re-run `cairn login` to start a fresh session."
      />
    )
  }

  if (status === STATUS.NOT_SIGNED_IN) {
    return (
      <Navigate
        to={`/auth/login?returnTo=${encodeURIComponent(`/auth/cli?port=${port}&state=${state}`)}`}
        replace
      />
    )
  }

  return (
    <Body
      title={titleFor(status)}
      body={bodyFor(status, errorMsg)}
      tone={status === STATUS.ERROR ? 'error' : 'normal'}
    />
  )
}

function titleFor(status) {
  switch (status) {
    case STATUS.IDLE:
    case STATUS.POSTING:
      return 'Signing you in to the CLI…'
    case STATUS.SUCCESS:
      return 'CLI sign-in complete'
    case STATUS.ERROR:
      return 'Could not complete CLI sign-in'
    default:
      return ''
  }
}

function bodyFor(status, errorMsg) {
  switch (status) {
    case STATUS.IDLE:
    case STATUS.POSTING:
      return 'Sending credentials to your terminal. This usually takes under a second.'
    case STATUS.SUCCESS:
      return 'You can close this tab and return to your terminal — the CLI has saved your session.'
    case STATUS.ERROR:
      return errorMsg ?? 'Something went wrong sending credentials to the CLI.'
    default:
      return ''
  }
}

function Body({ title, body, tone = 'normal' }) {
  return (
    <main id="main-content" className="mx-auto max-w-md px-6 py-16">
      <p className="mb-2 text-sm uppercase tracking-wider text-[var(--color-schist)]">
        cairn login
      </p>
      <h1 className="mb-4 text-2xl font-semibold text-[var(--color-basalt)]">{title}</h1>
      <p
        className={
          tone === 'error'
            ? 'mb-6 text-sm text-[var(--color-failed)]'
            : 'mb-6 text-sm text-[var(--color-slate)]'
        }
      >
        {body}
      </p>
      <Link
        to="/"
        className="text-sm text-[var(--color-terracotta)] underline-offset-4 hover:underline"
      >
        ← Back to home
      </Link>
    </main>
  )
}
